/**
 * The turntable inside the PERSONAL AIRCRAFT screen — *"The arrow keys on the
 * bottom left of the screen allow you to change the view of your aircraft. You
 * can zoom the camera in/out and rotate it left/right."* (manual, "Personal
 * Aircraft").
 *
 * It deliberately shares `flight/aircraft-mesh.ts` with `/fly` rather than
 * reimplementing a viewer: the whole point of the paint shop is that what the
 * pilot sees on this screen is what takes off, so the preview must go through
 * the same repaint and the same `BARON.PAL` ramp material the ace liveries and
 * the flown aeroplane do. Anything cheaper would be a picture of the paint
 * rather than the paint.
 *
 * That is also why it honours ENHANCED MODELS. The switch lives in the in-flight
 * controls row (`flight/effects-settings.ts`) and is read straight out of
 * storage here rather than threaded down through the career screens: the
 * turntable has no settings of its own, and a pilot who chose to fly enhanced
 * aeroplanes should be looking at the one he will take off in.
 */

import type { PersonalPaint } from "@red-opus/formats";
import * as THREE from "three/webgpu";
import { disposeTree } from "../three/dispose-tree.ts";
import { disableWebGlFallback } from "../three/webgpu-support.ts";
import { browserSettingsStore } from "../audio/settings.ts";
import { loadAircraftMesh } from "../flight/aircraft-mesh.ts";
import { DEFAULT_EFFECTS_SETTINGS, loadEffectsSettings } from "../flight/effects-settings.ts";
import { viewPixelRatio } from "../three/view-pixel-ratio.ts";
import { clampZoom, INITIAL_POSE, zoomDistance, type PreviewPose } from "./preview-pose.ts";

/** The pilot's current ENHANCED MODELS choice, or the default on a page with no storage. */
function enhancedModelsWanted(): boolean {
  const store = browserSettingsStore();
  return (store ? loadEffectsSettings(store) : DEFAULT_EFFECTS_SETTINGS).enhancedModels;
}

/** Looking slightly down on the aircraft, as the original's viewer does. */
const PITCH_RAD = -0.32;
const FOV_DEG = 30;

export interface AircraftPreview {
  /** Loads (or reloads) the aeroplane. Safe to call on every paint change. */
  show(modelId: string, paint: PersonalPaint | undefined): void;
  setPose(pose: PreviewPose): void;
  resize(width: number, height: number): void;
  dispose(): void;
}

interface PreviewState {
  readonly renderer: THREE.WebGPURenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly pivot: THREE.Group;
  radius: number;
  pose: PreviewPose;
  /** What `show` was last asked for, so an unchanged request does not refetch. */
  key: string;
  generation: number;
  disposed: boolean;
}

function place(state: PreviewState): void {
  const distance = zoomDistance(state.pose, state.radius);
  const yaw = (state.pose.yawDeg * Math.PI) / 180;
  state.camera.position.set(
    Math.sin(yaw) * Math.cos(PITCH_RAD) * distance,
    -Math.sin(PITCH_RAD) * distance,
    Math.cos(yaw) * Math.cos(PITCH_RAD) * distance,
  );
  state.camera.lookAt(0, 0, 0);
}

function render(state: PreviewState): void {
  if (state.disposed) return;
  void state.renderer.renderAsync(state.scene, state.camera);
}

/**
 * Attaches a preview to `container`. Returns `undefined` where WebGPU is not
 * available — the caller keeps its 2D screen and simply shows no turntable,
 * which is the same degradation `/fly` makes.
 */
export function createAircraftPreview(container: HTMLElement): AircraftPreview | undefined {
  let renderer: THREE.WebGPURenderer;
  try {
    renderer = new THREE.WebGPURenderer({ antialias: true, alpha: true });
  } catch {
    return undefined;
  }
  disableWebGlFallback(renderer);
  // The turntable has no settings UI of its own, so it reads the stored choice
  // the same way it reads it for the geometry: ENHANCED MODELS lifts the
  // resolution cap here too (`three/view-pixel-ratio.ts`). Taken at attach
  // rather than live, which is all this surface needs — the panel that flips
  // the setting is not reachable without leaving the screen and rebuilding it.
  renderer.setPixelRatio(viewPixelRatio(enhancedModelsWanted()));
  renderer.setClearColor(0x000000, 0);
  container.appendChild(renderer.domElement);
  renderer.domElement.style.width = "100%";
  renderer.domElement.style.height = "100%";
  renderer.domElement.style.display = "block";

  const state: PreviewState = {
    renderer,
    scene: new THREE.Scene(),
    camera: new THREE.PerspectiveCamera(FOV_DEG, 1, 0.1, 500),
    pivot: new THREE.Group(),
    radius: 6,
    pose: INITIAL_POSE,
    key: "",
    generation: 0,
    disposed: false,
  };
  state.scene.add(state.pivot);
  place(state);

  return {
    show(modelId, paint) {
      const enhanced = enhancedModelsWanted();
      const key = `${modelId}|${paint?.wings ?? "-"}|${paint?.fuselage ?? "-"}|${enhanced ? "enhanced" : "baked"}`;
      if (key === state.key) return;
      state.key = key;
      const generation = ++state.generation;
      void loadAircraftMesh(modelId, { paint, enhanced }).then((mesh) => {
        if (state.disposed || generation !== state.generation || !mesh) return;
        disposeTree(state.pivot);
        state.pivot.clear();
        state.pivot.add(mesh);
        const box = new THREE.Box3().setFromObject(mesh);
        state.radius = Math.max(0.5, box.getSize(new THREE.Vector3()).length() / 2);
        place(state);
        render(state);
      });
    },
    setPose(pose) {
      state.pose = { yawDeg: pose.yawDeg, zoom: clampZoom(pose.zoom) };
      place(state);
      render(state);
    },
    resize(width, height) {
      if (width < 1 || height < 1) return;
      state.renderer.setSize(width, height, false);
      state.camera.aspect = width / height;
      state.camera.updateProjectionMatrix();
      render(state);
    },
    dispose() {
      state.disposed = true;
      disposeTree(state.pivot);
      state.renderer.domElement.remove();
      state.renderer.dispose();
    },
  };
}
